import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Mic, MicOff, Loader2, ArrowLeft, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { JarvisOrb } from "@/components/jarvis/JarvisOrb";
import { JarvisTranscriptStream } from "@/components/jarvis/JarvisTranscriptStream";
import { InterventionStream } from "@/components/jarvis/InterventionStream";
import { ParticipantsPanel } from "@/components/jarvis/ParticipantsPanel";
import { MetricsPanel } from "@/components/jarvis/MetricsPanel";
import { SessionStatusBadge } from "@/components/jarvis/SessionStatusBadge";
import { SessionTimer } from "@/components/jarvis/SessionTimer";
import { PresenterControls } from "@/components/jarvis/PresenterControls";
import { useJarvisRuntime } from "@/hooks/useJarvisRuntime";
import { loadPersonaKey } from "@/components/jarvis/PersonaSelector";

type Mode = "passive" | "invocation" | "proactive";

type Session = {
  id: string;
  org_id: string;
  status: string;
  mode: string;
  event_name: string | null;
  started_at: string | null;
  ended_at: string | null;
  total_turns: number;
  total_cost_usd: number;
  total_llm_tokens: number;
  total_stt_ms: number;
  config: Record<string, unknown> | null;
};

export default function JarvisLiveSession() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>("invocation");
  const containerRef = useRef<HTMLDivElement>(null);

  // Carrega sessão + realtime de mudanças (status + métricas)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const load = async () => {
      const { data, error } = await supabase
        .from("jarvis_sessions")
        .select("id, org_id, status, mode, event_name, started_at, ended_at, total_turns, total_cost_usd, total_llm_tokens, total_stt_ms, config")
        .eq("id", id)
        .single();
      if (cancelled) return;
      if (error || !data) {
        toast.error("Sessão não encontrada");
        navigate("/jarvis/history", { replace: true });
        return;
      }
      setSession(data as Session);
      setMode((data.mode as Mode) ?? "invocation");
      setLoading(false);
    };
    void load();

    const ch = supabase
      .channel(`jarvis-session-${id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "jarvis_sessions", filter: `id=eq.${id}` },
        (payload) => setSession((prev) => prev ? { ...prev, ...(payload.new as Session) } : prev),
      )
      .subscribe();

    return () => { cancelled = true; void supabase.removeChannel(ch); };
  }, [id, navigate]);

  const wakeWords = useMemo(() => {
    const cfg = session?.config as { wake_words?: string[] } | null;
    return cfg?.wake_words?.length ? cfg.wake_words : ["jarvis", "syra"];
  }, [session?.config]);

  const runtime = useJarvisRuntime({
    sessionId: id ?? "",
    mode,
    wakeWords,
    personaKey: ((session?.config as { persona_key?: string } | null)?.persona_key as "syra" | "jarvis" | undefined) ?? loadPersonaKey(),
    onError: (e) => toast.error(`Jarvis: ${e}`),
  });

  const isTerminal = session?.status && ["completed", "abandoned", "error"].includes(session.status);

  const startMic = useCallback(async () => {
    if (!session) return;
    try {
      // Garantir que sessão esteja ouvindo
      if (!["listening", "thinking", "speaking", "paused", "interrupted"].includes(session.status)) {
        await supabase.functions.invoke("jarvis-session", {
          body: { action: "transition", session_id: session.id, new_status: "listening" },
        });
      }
      await runtime.start();
      toast.success("Jarvis ouvindo");
    } catch (e) {
      toast.error(`Erro ao iniciar microfone: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [session, runtime]);

  const stopMic = useCallback(() => {
    runtime.stop();
    toast.info("Microfone parado");
  }, [runtime]);

  const handleEnded = useCallback(() => {
    runtime.stop();
    toast.success("Sessão encerrada. Gerando artefatos…");
  }, [runtime]);

  if (loading || !session) {
    return (
      <div className="container mx-auto px-4 py-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const micRunning = runtime.state !== "idle" && runtime.state !== "error";

  return (
    <div ref={containerRef} className="container mx-auto px-4 py-6 max-w-7xl bg-background">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/jarvis"><ArrowLeft className="w-4 h-4 mr-1" /> Hub</Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-headline">
              {session.event_name || "Sessão Jarvis"}
            </h1>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <SessionStatusBadge status={session.status} />
              <Badge variant="outline" className="capitalize">{mode}</Badge>
              <SessionTimer startedAt={session.started_at} endedAt={session.ended_at} />
              <code className="text-[10px] text-muted-foreground font-mono">{session.id.slice(0, 8)}</code>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isTerminal ? (
            <Button asChild>
              <Link to={`/jarvis/session/${session.id}/review`}>
                <FileText className="w-4 h-4 mr-1" /> Ver review
              </Link>
            </Button>
          ) : (
            <PresenterControls
              sessionId={session.id}
              status={session.status}
              mode={mode}
              onModeChange={setMode}
              onEnded={handleEnded}
              fullscreenTargetRef={containerRef}
            />
          )}
        </div>
      </div>

      {runtime.ttsWarning && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>Falha na síntese de voz</AlertTitle>
          <AlertDescription>
            O Jarvis está respondendo apenas em texto. Motivo: <code className="text-xs">{runtime.ttsWarning.reason ?? runtime.ttsWarning.status}</code>
          </AlertDescription>
        </Alert>
      )}

      {/* Body 2 colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        {/* Esquerda: Orb + Transcript + Interventions */}
        <div className="space-y-4 min-w-0">
          <Card className="p-4 sm:p-6 flex flex-col items-center">
            <JarvisOrb state={runtime.state} level={runtime.level} size={150} />
            <div className="mt-4">
              {!micRunning ? (
                <Button onClick={startMic} disabled={isTerminal} size="lg">
                  <Mic className="w-4 h-4 mr-2" /> Ativar microfone
                </Button>
              ) : (
                <Button onClick={stopMic} variant="secondary" size="lg">
                  <MicOff className="w-4 h-4 mr-2" /> Parar microfone
                </Button>
              )}
            </div>
            {runtime.lastResponse && (
              <div className="w-full mt-4 p-3 rounded-md bg-muted/40 border border-border/50">
                <div className="text-[10px] text-muted-foreground mb-1 flex justify-between">
                  <span>Última resposta</span>
                  <span className="tabular-nums">{runtime.lastResponse.e2eMs}ms · {runtime.lastResponse.provider}</span>
                </div>
                <p className="text-sm line-clamp-3">{runtime.lastResponse.text}</p>
              </div>
            )}
          </Card>

          <Card className="p-4 min-h-[280px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Transcrição ao vivo</h2>
              <Badge variant="outline" className="text-[10px]">Realtime</Badge>
            </div>
            <div className="flex-1 min-h-0">
              <JarvisTranscriptStream sessionId={session.id} ttsWarning={runtime.ttsWarning} />
            </div>
          </Card>
        </div>

        {/* Direita: Interventions + Participants + Metrics */}
        <div className="space-y-4">
          <Card className="p-4 min-h-[240px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Intervenções do Jarvis</h2>
            </div>
            <div className="flex-1 min-h-0">
              <InterventionStream sessionId={session.id} />
            </div>
          </Card>
          <ParticipantsPanel sessionId={session.id} />
          <MetricsPanel
            stats={{
              total_turns: session.total_turns,
              total_cost_usd: session.total_cost_usd,
              total_llm_tokens: session.total_llm_tokens,
              total_stt_ms: session.total_stt_ms,
            }}
            lastTurn={runtime.lastResponse?.latency ?? null}
          />
        </div>
      </div>
    </div>
  );
}
